"use client";

import type { ReactNode } from "react";
import { useMemo } from "react";

import Image from "next/image";

import type { EmblaCarouselType } from "embla-carousel";
import type { AutoplayOptionsType } from "embla-carousel-autoplay";
import Autoplay from "embla-carousel-autoplay";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";

import carouselArrowLeft from "@repo/ui/assets/HomePage/carousel-arrow-left.svg";
import carouselArrowRight from "@repo/ui/assets/HomePage/carousel-arrow-right.svg";
import { cn } from "@repo/ui/lib/utils";

import { DotButton } from "./HeroCarouselButton";
import { useDotButton } from "./hooks/useDotButton";

type CarouselItem = {
  id: string | number;
  content: ReactNode;
};

type CarouselProps = {
  items: CarouselItem[];
  autoplayOptions?: AutoplayOptionsType;
  className?: string;
  slideClassName?: string;
  arrowsClassName?: string;
  showDotControls?: boolean;
};

export default function HeroCarousel({
  items,
  autoplayOptions = { delay: 4000 },
  className,
  slideClassName,
  arrowsClassName,
  showDotControls = false,
}: Readonly<CarouselProps>) {
  const autoplay = useMemo(() => Autoplay(autoplayOptions), [autoplayOptions]);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true }, [autoplay]);

  const onNavButtonClick = (_emblaApi: EmblaCarouselType) => {
    if (!autoplay) return;
    const resetOrStop =
      (autoplay.options as AutoplayOptionsType).stopOnInteraction === false
        ? autoplay.reset
        : autoplay.stop;
    resetOrStop();
  };

  const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(emblaApi, onNavButtonClick);

  return (
    <div
      className={cn(
        "relative w-full lg:max-w-[1440px] mx-auto overflow-hidden rounded-b-xl bg-transparent",
        className,
      )}
    >
      <div ref={emblaRef}>
        <div className="flex">
          {items.map((item) => (
            <div
              key={item.id}
              className={cn(
                "relative flex-[1_0_100%]",
                slideClassName || "aspect-[16/9] max-h-[560px] min-h-[380px]",
              )}
            >
              {item.content}
            </div>
          ))}
        </div>
      </div>

      {/* Side arrows — hidden on mobile when the combined pill (below) is in use,
          since that already has its own prev/next buttons. */}
      <div
        className={cn(
          "px-4 sm:px-6 lg:px-10 absolute top-1/2 right-0 left-0 z-10 mx-auto flex -translate-y-1/2 justify-between",
          showDotControls && "hidden md:flex",
          arrowsClassName,
        )}
      >
        <Image
          src={carouselArrowLeft}
          height={40}
          width={40}
          alt="carousel-arrow-left"
          onClick={() => emblaApi?.scrollPrev()}
          className="cursor-pointer aspect-square w-[30px] md:w-[40px]"
        />
        <Image
          src={carouselArrowRight}
          height={40}
          width={40}
          alt="carousel-arrow-right"
          onClick={() => emblaApi?.scrollNext()}
          className="cursor-pointer aspect-square w-[30px] md:w-[40px]"
        />
        {/* <ChevronLeft
          onClick={() => emblaApi?.scrollPrev()}
          className="h-6 w-6 -translate-x-4 cursor-pointer text-white/80 md:h-10 md:w-10"
        />
        <ChevronRight
          onClick={() => emblaApi?.scrollNext()}
          className="h-6 w-6 translate-x-4 cursor-pointer text-white/80 md:h-10 md:w-10"
        /> */}
      </div>

      {/* Mobile nav pill (Figma spec) — bottom-anchored so it adapts to container height. */}
      {showDotControls && (
        <div className="absolute left-1/2 bottom-4 z-10 flex h-[30px] w-[160px] -translate-x-1/2 items-center justify-center gap-2.5 rounded-full bg-white/40 shadow-[0px_0px_4px_rgba(0,0,0,0.25)] backdrop-blur-[1.5px] md:!hidden">
          <button
            type="button"
            onClick={() => emblaApi?.scrollPrev()}
            aria-label="Previous slide"
            className="flex h-[30px] w-[30px] shrink-0 cursor-pointer items-center justify-center rounded-full"
          >
            <ChevronLeft className="text-brand-turquoise h-4 w-4" aria-hidden="true" />
          </button>

          <div className="flex h-5 w-20 shrink-0 items-center justify-center gap-[5px] rounded-full backdrop-blur-[1.5px]">
            {scrollSnaps.map((_, index) => (
              <DotButton
                key={index}
                onClick={() => onDotButtonClick(index)}
                className="m-0 flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center border-0 p-0"
              >
                <span
                  className={cn(
                    "bg-brand-turquoise h-2.5 w-2.5 rounded-full",
                    index === selectedIndex && "bg-brand-turquoise-40 w-5",
                  )}
                />
              </DotButton>
            ))}
          </div>

          <button
            type="button"
            onClick={() => emblaApi?.scrollNext()}
            aria-label="Next slide"
            className="flex h-[30px] w-[30px] shrink-0 cursor-pointer items-center justify-center rounded-full"
          >
            <ChevronRight className="text-brand-turquoise-40 h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      )}
    </div>
  );
}
